import { useState, useCallback, useEffect, useRef } from 'react';

const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_LIMIT = 20;

export interface AsyncDropdownResponse<T> {
  data: T[];
  hasMore?: boolean;
  total?: number;
  page?: number;
  limit?: number;
}

export interface UseAsyncDropdownOptions<T> {
  /** Fetches a page of items. Receives abort signal for cancellation. */
  fetchFn: (params: { page: number; search: string; limit: number; signal?: AbortSignal }) => Promise<AsyncDropdownResponse<T>>;
  limit?: number;
  debounceMs?: number;
  /** Stable id for the selected item (e.g. item.id). Used to dedupe when appending. */
  getItemId?: (item: T) => string;
  /** Call when company/location context changes to reset. */
  resetDeps?: unknown[];
}

export interface UseAsyncDropdownReturn<T> {
  items: T[];
  page: number;
  limit: number;
  hasMore: boolean;
  isLoading: boolean;
  searchTerm: string;
  debouncedSearchTerm: string;
  setSearchTerm: (v: string) => void;
  /** Call when dropdown opens: load page 1 with no search. */
  loadInitial: () => void;
  /** Call to load next page (infinite scroll). */
  loadMore: () => void;
  /** Reset items and state (e.g. when context changes). */
  reset: () => void;
}

export function useAsyncDropdown<T>({
  fetchFn,
  limit = DEFAULT_LIMIT,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  getItemId = (item: any) => (item?.id ?? String(item)),
  resetDeps = [],
}: UseAsyncDropdownOptions<T>): UseAsyncDropdownReturn<T> {
  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTermState] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);
  const isLoadingRef = useRef(false);
  const prevDebouncedRef = useRef(debouncedSearchTerm);
  const isFirstRenderRef = useRef(true);

  const setSearchTerm = useCallback((v: string) => {
    setSearchTermState(v);
  }, []);

  const loadPage = useCallback(
    async (pageNum: number, search: string, append: boolean) => {
      if (isLoadingRef.current) return;
      const reqId = ++requestIdRef.current;
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;
      isLoadingRef.current = true;
      setIsLoading(true);
      try {
        const res = await fetchFn({
          page: pageNum,
          search,
          limit,
          signal,
        });
        if (signal.aborted || requestIdRef.current !== reqId) return;
        const data = res.data ?? [];
        const hasMoreRes =
          res.hasMore !== undefined
            ? res.hasMore
            : res.total !== undefined
              ? (append ? items.length : 0) + data.length < res.total
              : data.length >= limit;
        if (append) {
          setItems((prev) => {
            const seen = new Set(prev.map(getItemId));
            const next = [...prev];
            for (const item of data) {
              const id = getItemId(item);
              if (!seen.has(id)) {
                seen.add(id);
                next.push(item);
              }
            }
            return next;
          });
          setPage((p) => p + 1);
        } else {
          setItems(data);
          setPage(1);
        }
        setHasMore(hasMoreRes);
      } catch (err: any) {
        if (err?.name === 'AbortError' || signal.aborted || requestIdRef.current !== reqId) return;
        console.error('useAsyncDropdown fetch error:', err);
      } finally {
        if (requestIdRef.current === reqId) {
          isLoadingRef.current = false;
          setIsLoading(false);
        }
      }
    },
    [fetchFn, limit, getItemId, items.length]
  );

  const loadInitial = useCallback(() => {
    setSearchTermState('');
    setDebouncedSearchTerm('');
    setPage(1);
    setHasMore(false);
    prevDebouncedRef.current = '';
    requestIdRef.current += 1;
    loadPage(1, '', false);
  }, [loadPage]);

  const loadMore = useCallback(() => {
    if (isLoadingRef.current || !hasMore) return;
    loadPage(page + 1, debouncedSearchTerm, true);
  }, [loadPage, page, debouncedSearchTerm, hasMore]);

  const reset = useCallback(() => {
    abortControllerRef.current?.abort();
    setItems([]);
    setPage(1);
    setHasMore(false);
    setSearchTermState('');
    setDebouncedSearchTerm('');
    requestIdRef.current += 1;
  }, []);

  // Debounce search term
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, debounceMs);
    return () => clearTimeout(t);
  }, [searchTerm, debounceMs]);

  // When debounced search term changes: reset and fetch page 1 (except initial mount / loadInitial)
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    if (prevDebouncedRef.current === debouncedSearchTerm) return;
    prevDebouncedRef.current = debouncedSearchTerm;
    setPage(1);
    setItems([]);
    setHasMore(false);
    loadPage(1, debouncedSearchTerm, false);
  }, [debouncedSearchTerm, loadPage]);

  // Reset when deps change (e.g. companyId)
  useEffect(() => {
    reset();
  }, resetDeps);

  return {
    items,
    page,
    limit,
    hasMore,
    isLoading,
    searchTerm,
    debouncedSearchTerm,
    setSearchTerm,
    loadInitial,
    loadMore,
    reset,
  };
}
